import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { HeartIcon } from "../lib/icons";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useNoIndex } from "../lib/useNoIndex";
import { DonorSurveyDialog } from "../components/survey/DonorSurveyDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Poll cadence — Stripe webhooks are usually near-instant, but the poll is a
// safety net for environments where the webhook can't reach us.
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15;   // ~30 seconds

const fmtCents = (c) => {
  if (typeof c !== "number") return "";
  return c.toLocaleString("en-US", { style: "currency", currency: "USD" }).replace(/\.00$/, "");
};

/**
 * PaymentSuccess — thank-you page reached after a completed Stripe Checkout.
 *
 * Reads ?session_id=... and polls /api/donations/status until we see `paid`
 * (webhook has landed) or the poll budget is spent (we still show a friendly
 * message — the DB record will settle within seconds regardless).
 */
export default function PaymentSuccess() {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const isFounding = searchParams.get("founding") === "1";

  const [state, setState] = useState("loading");  // loading | paid | still_pending | failed | error
  const [amountCents, setAmountCents] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      attempt += 1;
      setAttempts(attempt);
      try {
        const r = await axios.get(`${API}/donations/status/${sessionId}`, { withCredentials: true });
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          setState("paid");
          // amount comes back on the record (via the finalise step)
          if (typeof r.data.amount_total_cents === "number") setAmountCents(r.data.amount_total_cents);
          return;
        }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          setState("failed");
          return;
        }
        if (attempt >= POLL_MAX_ATTEMPTS) {
          setState("still_pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="bg-sand">
      <Navbar
        onStart={() => (window.location.href = "/")}
        primaryLabel="Back to home"
        secondaryLabel={null}
      />

      <section className="mx-auto flex min-h-[75vh] max-w-2xl flex-col justify-center px-6 py-24 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl border border-ink/10 bg-bone p-10 shadow-[0_20px_60px_-20px_rgba(26,23,20,0.35)] lg:p-14"
        >
          {state === "loading" && (
            <div className="text-center">
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-olive" />
              <p className="mt-6 font-serif text-3xl text-ink">Confirming your gift…</p>
              <p className="mt-2 text-[13px] text-olive">Attempt {attempts} of {POLL_MAX_ATTEMPTS}. This usually takes a second.</p>
            </div>
          )}

          {state === "paid" && (
            <div className="text-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-grass/15 text-grass">
                <CheckCircle2 className="h-8 w-8" strokeWidth={2} />
              </span>
              <p className="mt-6 text-[13px] font-semibold uppercase tracking-[0.28em] text-grass">
                {isFounding ? "Welcome to the wall" : "Thank you"}
              </p>
              <h1 className="mt-2 font-serif text-3xl lg:text-[3.5rem] leading-tight text-ink">
                {isFounding
                  ? "You’re a founding donor."
                  : (amountCents ? `Your ${fmtCents(amountCents / 100)} gift landed.` : "Your gift landed.")}
              </h1>
              {isFounding ? (
                <p className="mx-auto mt-4 max-w-md text-base text-olive">
                  Your name goes on the founding donors wall{amountCents ? ` — ${fmtCents(amountCents / 100)} received` : ""}. Stripe just sent your receipt.
                  A warm welcome note is on its way with the tribute and photo you shared.
                  On behalf of every student who’ll be helped because you believed <span className="italic">first</span>: thank you.
                </p>
              ) : (
                <p className="mx-auto mt-4 max-w-md text-base text-olive">
                  Your gift was made to My Rich Aunt, and your receipt is on its way. You&rsquo;ll see the campaign&rsquo;s progress
                  update within a minute. On behalf of a student who was worried
                  about the number: <span className="italic">thank you</span>.
                </p>
              )}
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                {isFounding ? (
                  <Link
                    to="/founding"
                    data-testid="payment-success-view-wall"
                    className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand transition-colors hover:bg-forest"
                  >
                    See the wall <ArrowRight className="h-4 w-4" strokeWidth={2} />
                  </Link>
                ) : (
                  <Link
                    to="/#campaigns"
                    data-testid="payment-success-view-campaigns"
                    className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand transition-colors hover:bg-forest"
                  >
                    Meet more students <ArrowRight className="h-4 w-4" strokeWidth={2} />
                  </Link>
                )}
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-sand px-6 py-3 text-base font-semibold text-ink hover:border-ink"
                >
                  Back to home
                </Link>
              </div>
            </div>
          )}

          {state === "still_pending" && (
            <div className="text-center">
              <HeartIcon className="mx-auto h-10 w-10 text-tangerine" />
              <p className="mt-6 font-serif text-3xl text-ink">Your card&rsquo;s being confirmed.</p>
              <p className="mx-auto mt-3 max-w-md text-[13px] text-olive">
                Stripe hasn&rsquo;t sent us the final confirmation yet. Your receipt will still
                arrive — usually within a minute — and you can safely close this page.
                We&rsquo;ll credit your gift the moment it clears.
              </p>
              <div className="mt-8 flex items-center justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand hover:bg-forest"
                >
                  Back to home
                </Link>
              </div>
            </div>
          )}

          {(state === "failed" || state === "error") && (
            <div className="text-center">
              <AlertTriangle className="mx-auto h-10 w-10 text-scarlet" strokeWidth={1.5} />
              <p className="mt-6 font-serif text-3xl text-ink">Something&rsquo;s off with that gift.</p>
              <p className="mx-auto mt-3 max-w-md text-[13px] text-olive">
                {state === "failed"
                  ? "Your card was declined or the session expired before it went through. Nothing was charged."
                  : "We couldn't check on your payment. If your card was charged, you'll still get a receipt — reach out and we'll sort it."}
              </p>
              <div className="mt-8 flex items-center justify-center gap-3">
                <Link
                  to="/#campaigns"
                  className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-base font-semibold text-sand hover:bg-forest"
                >
                  Try a different gift
                </Link>
              </div>
            </div>
          )}
        </motion.div>
      </section>

      <Footer variant="minimal" />
      <DonorSurveyDialog sessionId={sessionId} active={state === "paid" && !isFounding} />
    </main>
  );
}
